import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, Filter, BellRing, BellOff } from 'lucide-react';
import clsx from 'clsx';
import { useNotifications } from '@/hooks/useNotifications';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useAuth } from '@/context/AuthContext';
import type { Notification, NotificationType } from '@/db/notifications';
import {
  isPushSupported,
  getPermissionState,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed as checkIsSubscribed,
  sendTestNotification,
} from '@/lib/pushNotifications';

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  mention: 'Menzione',
  assigned: 'Assegnazione',
  unassigned: 'Rimosso',
  commented: 'Commento',
  status_changed: 'Status cambiato',
  due_soon: 'Scadenza vicina',
  overdue: 'In ritardo',
  sprint_started: 'Sprint iniziato',
  sprint_ended: 'Sprint terminato',
  task_created: 'Task creato',
  task_completed: 'Task completato',
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'ora';
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  return `${days}g fa`;
}

function NotificationItem({ notification, onMarkAsRead }: { notification: Notification; onMarkAsRead: (id: string) => void }) {
  return (
    <button
      onClick={() => !notification.read && onMarkAsRead(notification.id)}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-border-primary transition-colors',
        notification.read
          ? 'bg-transparent'
          : 'bg-accent-violet/5 hover:bg-accent-violet/10',
      )}
    >
      <div className="flex items-start gap-2">
        {!notification.read && (
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent-violet shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-text-quaternary">
              {NOTIFICATION_TYPE_LABELS[notification.type] || notification.type}
            </span>
            <span className="text-[10px] text-text-quaternary">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>
          <p className="text-[11px] text-text-secondary truncate">
            {notification.message || 'Nuova notifica'}
          </p>
        </div>
      </div>
    </button>
  );
}

const FILTER_OPTIONS: { label: string; types: NotificationType[] }[] = [
  { label: 'Menzioni', types: ['mention'] },
  { label: 'Assegnazioni', types: ['assigned', 'unassigned'] },
  { label: 'Commenti', types: ['commented'] },
  { label: 'Scadenze', types: ['due_soon', 'overdue'] },
  { label: 'Status', types: ['status_changed'] },
  { label: 'Task', types: ['task_created', 'task_completed'] },
];

export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, filterTypes, setFilterTypes } = useNotifications();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pushSupported] = useState(() => isPushSupported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useClickOutside([panelRef], () => setIsOpen(false), isOpen);

  // Check push subscription state on mount
  useEffect(() => {
    if (pushSupported) {
      checkIsSubscribed().then(setPushEnabled);
    }
  }, [pushSupported]);

  const handleTogglePush = useCallback(async () => {
    if (!user || pushLoading) return;
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush(user.id);
        setPushEnabled(false);
      } else {
        const sub = await subscribeToPush(user.id);
        setPushEnabled(!!sub);
        if (sub) {
          // Send test notification to confirm it works
          await sendTestNotification();
        }
      }
    } catch {
      // Permission denied or other error
    } finally {
      setPushLoading(false);
    }
  }, [user, pushEnabled, pushLoading]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'relative p-1.5 rounded-md transition-colors',
          isOpen
            ? 'bg-accent-violet-dim text-accent-violet'
            : 'text-text-quaternary hover:bg-surface-hover hover:text-text-tertiary',
        )}
        title="Notifiche"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-violet text-white text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-text-primary">Notifiche</span>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'p-0.5 rounded transition-colors',
                  showFilters || filterTypes.length > 0
                    ? 'text-accent-violet bg-accent-violet/10'
                    : 'text-text-quaternary hover:text-text-tertiary',
                )}
                title="Filtra per tipo"
              >
                <Filter className="w-3 h-3" />
              </button>
              {pushSupported && (
                <button
                  onClick={handleTogglePush}
                  disabled={pushLoading || getPermissionState() === 'denied'}
                  className={clsx(
                    'p-0.5 rounded transition-colors',
                    pushEnabled
                      ? 'text-accent-emerald bg-accent-emerald/10'
                      : 'text-text-quaternary hover:text-text-tertiary',
                    pushLoading && 'opacity-50',
                    getPermissionState() === 'denied' && 'opacity-30 cursor-not-allowed',
                  )}
                  title={
                    getPermissionState() === 'denied'
                      ? 'Notifiche bloccate dal browser'
                      : pushEnabled
                        ? 'Disattiva notifiche push'
                        : 'Attiva notifiche push'
                  }
                >
                  {pushEnabled ? <BellRing className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                </button>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-[10px] text-accent-violet hover:text-accent-violet/80 transition-colors"
              >
                <CheckCheck className="w-3 h-3" />
                Segna tutte lette
              </button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-border-primary">
              {FILTER_OPTIONS.map(f => {
                const isActive = f.types.every(t => filterTypes.includes(t));
                return (
                  <button
                    key={f.label}
                    onClick={() => {
                      if (isActive) {
                        setFilterTypes(prev => prev.filter(t => !f.types.includes(t)));
                      } else {
                        setFilterTypes(prev => [...prev, ...f.types.filter(t => !prev.includes(t))]);
                      }
                    }}
                    className={clsx(
                      'px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors',
                      isActive
                        ? 'bg-accent-violet/15 border-accent-violet/30 text-accent-violet'
                        : 'bg-surface-primary border-border-primary text-text-quaternary hover:text-text-secondary',
                    )}
                  >
                    {f.label}
                  </button>
                );
              })}
              {filterTypes.length > 0 && (
                <button
                  onClick={() => setFilterTypes([])}
                  className="px-2 py-0.5 text-[10px] text-text-quaternary hover:text-text-secondary transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
          )}

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <Check className="w-5 h-5 text-text-quaternary mx-auto mb-2" />
                <p className="text-[11px] text-text-quaternary">Nessuna notifica</p>
              </div>
            ) : (
              notifications.map(n => (
                <NotificationItem key={n.id} notification={n} onMarkAsRead={markAsRead} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
